import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Slider } from '@atlas/components';
import { SiteConfigManager } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { Label } from '@atlas/ui';

import history from 'components/Layout/history';
import { CDN_TYPE_GAME, imageUrlCDN } from 'src/common/utils';
import { useIsRtl } from 'src/hooks/useIsRtl';

const ProvidersBlockSlider = ({
  onProviderClick,
  propSliderSettings = { rows: 2 },
  providers,
  setSwiper,
}) => {
  const swiperRef = useRef(null);
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
  const isRtl = useIsRtl();
  const { rows } = propSliderSettings;
  const [showSwiper, setShowSwiper] = useState(true);

  const sliderConfig = useMemo(
    () => ({
      dir: isRtl ? 'rtl' : 'ltr',
      grid: {
        fill: 'row',
        rows: rows,
      },
    }),
    [isRtl, rows],
  );

  const responsiveSliderSettings = useMemo(() => {
    return {
      0: {
        grid: {
          rows: rows,
        },
        slidesPerView: 6,
      },
      980: {
        grid: {
          rows: rows,
        },
        slidesPerView: 8,
      },
      1176: {
        grid: {
          rows: rows,
        },
        slidesPerView: 10,
      },
      1377: {
        grid: {
          rows: rows,
        },
        slidesPerView: 13,
      },
    };
  }, [rows]);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
      setShowSwiper(false); // unmount component
      setTimeout(() => setShowSwiper(true), 0);
    }
  }, [isRtl]);

  const onClickHandler = useCallback(
    provider => {
      onProviderClick();
      history.push(`${casinoPath}?providerNames=${provider.name}`);
    },
    [casinoPath, onProviderClick],
  );

  if (!showSwiper) {
    return null;
  }

  return (
    <Slider
      autoplay={false}
      breakpoints={responsiveSliderSettings}
      breakpointsBase="container"
      className="casino-providers-block__slider"
      dir={sliderConfig.dir}
      grid={sliderConfig.grid}
      loop={false}
      onBeforeInit={setSwiper}
      onSwiper={swiper => (swiperRef.current = swiper)}
      slidesPerView={6}
      spaceBetween={7}
    >
      {providers.map(provider => (
        <Slider.Slide key={provider.id}>
          <div
            className="provider-card casino-providers-block__slider-item"
            onClick={() => onClickHandler(provider)}
          >
            {provider?.iconUrl && (
              <img
                alt={provider.providerName}
                className="provider-card__img"
                loading="lazy"
                src={`${imageUrlCDN(provider.iconUrl, CDN_TYPE_GAME)}`}
              />
            )}
            {!provider?.iconUrl && (
              <Label className="provider-card__name" message={provider.displayedName} />
            )}
          </div>
        </Slider.Slide>
      ))}
    </Slider>
  );
};

export default ProvidersBlockSlider;

ProvidersBlockSlider.propTypes = {
  onProviderClick: PropTypes.func.isRequired,
  propSliderSettings: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.number),
  }),
  providers: PropTypes.array.isRequired,
  setSwiper: PropTypes.func.isRequired,
};
